import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/axios';
import { formatNumber } from '../utils/formatUtils';

const AutoTradingStrategy = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [topStocks, setTopStocks] = useState([]);
  const [trendAnalysis, setTrendAnalysis] = useState([]);
  const [trendDates, setTrendDates] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  
  // 오늘 날짜 형식 (MM월DD일) - useMemo로 계산
  const todayDateString = useMemo(() => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    return `${month}월${day}일`;
  }, []);
  
  // 오늘 날짜를 YYYY-MM-DD 형식으로
  const getTodayDateInput = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  
  const [selectedDate, setSelectedDate] = useState(getTodayDateInput);
  const [currentDate, setCurrentDate] = useState(todayDateString);
  
  // 테마별로 그룹화
  const stocksByTheme = useMemo(() => {
    const grouped = {};
    topStocks.forEach((stock) => {
      const theme = stock.sector || '기타';
      if (!grouped[theme]) {
        grouped[theme] = [];
      }
      grouped[theme].push(stock);
    });
    // 각 테마별로 거래대금 내림차순 정렬
    Object.keys(grouped).forEach((theme) => {
      grouped[theme].sort((a, b) => b.tradingValue - a.tradingValue);
    });
    return grouped;
  }, [topStocks]);

  // 구독 상태 확인 (기본값 'N')
  const isSubscribed = user?.subscription === 'Y';

  // 선택한 날짜의 거래대금 상위 종목 가져오기 (DB에서)
  const fetchDailyTradingStocks = useCallback(async (date = null) => {
    try {
      setLoading(true);
      setError(null);
      const dateToFetch = date || selectedDate;
      const response = await apiClient.get('/market/daily-trading-stocks', {
        params: { date: dateToFetch }
      });
      // 응답 구조 확인 (디버깅)
      console.log('[프론트엔드] 일일 거래대금 상위 종목 API 응답:', response.data);
      const stocks = response.data?.stocks || [];
      console.log('[프론트엔드] 종목 개수:', stocks.length);
      if (stocks.length > 0) {
        console.log('[프론트엔드] 첫 번째 종목:', stocks[0]);
      }
      setTopStocks(stocks);
      // 날짜 설정 (YYYY-MM-DD 형식에서 MM월DD일 추출)
      if (response.data?.date) {
        // ISO 날짜 문자열 (예: "2025-12-29")에서 월-일 추출
        const dateStr = response.data.date;
        const parts = dateStr.split('-');
        if (parts.length >= 3) {
          const month = parseInt(parts[1], 10);
          const day = parseInt(parts[2], 10);
          setCurrentDate(`${month}월${day}일`);
        } else {
          setCurrentDate(todayDateString);
        }
      } else {
        setCurrentDate(todayDateString);
      }
    } catch (err) {
      console.error('[일일 거래대금 상위 종목] 조회 실패:', err);
      setError(err.response?.data?.error || '일일 거래대금 상위 종목 조회 중 오류가 발생했습니다.');
      setTopStocks([]);
      setCurrentDate(todayDateString);
    } finally {
      setLoading(false);
    }
  }, [todayDateString, selectedDate]);
  
  // 날짜 변경 핸들러
  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    // 날짜가 변경되면 자동으로 데이터 조회
    if (isSubscribed) {
      fetchDailyTradingStocks(newDate);
    }
  };

  // 추세분석 데이터 가져오기 (최근 15일치)
  const fetchTrendAnalysis = useCallback(async () => {
    if (!isSubscribed) return;
    
    try {
      setTrendLoading(true);
      const dates = [];
      const today = new Date();
      
      // 최근 15일치 날짜 생성
      for (let i = 0; i < 15; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
      }
      
      // 날짜를 오래된 순서대로 정렬 (역순)
      dates.reverse();
      
      // 각 날짜별 데이터 가져오기
      const allData = await Promise.all(
        dates.map(date => 
          apiClient.get('/market/daily-trading-stocks', { params: { date } })
            .then(res => ({ date, stocks: res.data?.stocks || [] }))
            .catch(() => ({ date, stocks: [] }))
        )
      );
      
      // 데이터가 있는 날짜만 필터링 (거래일만 포함)
      const tradingDates = allData
        .filter(({ stocks }) => stocks && stocks.length > 0)
        .map(({ date }) => date);
      
      // 거래일만 설정
      setTrendDates(tradingDates);
      
      // 종목별로 그룹화하고 순위, 가격 정보 저장
      const stockMap = new Map();
      
      allData
        .filter(({ stocks }) => stocks && stocks.length > 0) // 거래일만 처리
        .forEach(({ date, stocks }) => {
        // 거래대금 순으로 정렬하여 순위 부여
        const sortedStocks = [...stocks].sort((a, b) => b.tradingValue - a.tradingValue);
        
        sortedStocks.forEach((stock, index) => {
          const key = stock.stockCode;
          if (!stockMap.has(key)) {
            stockMap.set(key, {
              stockCode: stock.stockCode,
              stockName: stock.stockName,
              dataByDate: {}
            });
          }
          
          stockMap.get(key).dataByDate[date] = {
            rank: index + 1,
            price: stock.price || 0,
            prevChange: stock.prevChange || 0
          };
        });
      });
      
      // 전일 대비 등락률 계산
      const trendData = Array.from(stockMap.values())
        .map(stock => {
          const changeRates = {};
          for (let i = 1; i < dates.length; i++) {
            const prevDate = dates[i - 1];
            const currentDate = dates[i];
            const prevData = stock.dataByDate[prevDate];
            const currentData = stock.dataByDate[currentDate];
            
            if (prevData && currentData && Math.abs(prevData.price) > 0 && Math.abs(currentData.price) > 0) {
              // 하락일 때 가격 앞에 -가 붙을 수 있으므로 절대값 사용
              const prevPrice = Math.abs(prevData.price);
              const currentPrice = Math.abs(currentData.price);
              
              // 전일 대비 등락률 계산: (당일가격 - 전일가격) / 전일가격 * 100
              // 상승 시 양수, 하락 시 음수
              const rawChangeRate = ((currentPrice - prevPrice) / prevPrice) * 100;
              
              // 절대값으로 저장하되, 상승/하락 정보는 원래 부호로 판단
              const absChangeRate = Math.abs(rawChangeRate);
              const isUp = rawChangeRate > 0;
              const isDown = rawChangeRate < 0;
              
              changeRates[currentDate] = {
                value: absChangeRate, // 절대값으로 저장
                isUp: isUp,
                isDown: isDown
              };
            }
          }
          
          return {
            ...stock,
            changeRates
          };
        })
        .filter(stock => Object.keys(stock.dataByDate).length > 0)
        .sort((a, b) => {
          // 가장 최근 날짜의 순위로 정렬
          const lastDate = dates[dates.length - 1];
          const aRank = a.dataByDate[lastDate]?.rank || 9999;
          const bRank = b.dataByDate[lastDate]?.rank || 9999;
          return aRank - bRank;
        })
        .slice(0, 50); // 상위 50개만 표시
      
      setTrendAnalysis(trendData);
    } catch (err) {
      console.error('[추세분석] 조회 실패:', err);
    } finally {
      setTrendLoading(false);
    }
  }, [isSubscribed]);
  
  // 페이지 로드 시 자동으로 데이터 가져오기
  useEffect(() => {
    if (isSubscribed) {
      fetchDailyTradingStocks();
      fetchTrendAnalysis();
    }
  }, [isSubscribed, fetchDailyTradingStocks, fetchTrendAnalysis]);

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* 추세분석 테이블 - 완전 분리된 Paper */}
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShowChartIcon sx={{ fontSize: '1.35rem', color: 'text.secondary' }} aria-hidden />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                {getTodayDateInput} Trend Following
              </Typography>
            </Box>
            {trendLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : trendAnalysis.length > 0 ? (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell align="center" sx={{ fontWeight: 'bold', position: 'sticky', left: 0, zIndex: 1, bgcolor: 'action.hover' }}>
                        종목
                      </TableCell>
                      {trendDates.map((date) => {
                        const dateParts = date.split('-');
                        const day = parseInt(dateParts[2]);
                        return (
                          <TableCell key={date} align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                            {day}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trendAnalysis.map((stock) => (
                      <TableRow key={stock.stockCode} hover>
                        <TableCell 
                          align="center" 
                          sx={{ 
                            position: 'sticky', 
                            left: 0, 
                            zIndex: 1, 
                            bgcolor: 'background.paper' 
                          }}
                        >
                          {stock.stockName}
                        </TableCell>
                        {trendDates.map((date, idx) => {
                          const dateData = stock.dataByDate[date];
                          const changeRateInfo = stock.changeRates[date];
                          const hasChangeRate = changeRateInfo !== undefined;
                          const isUp = hasChangeRate && changeRateInfo.isUp;
                          const isDown = hasChangeRate && changeRateInfo.isDown;
                          const changeRateValue = hasChangeRate ? changeRateInfo.value : 0;
                          
                          // 전일 비교 데이터가 없을 때 가격 부호로 색상 결정
                          const price = dateData?.price || 0;
                          const priceIsNegative = price < 0;
                          const rankColor = hasChangeRate 
                            ? undefined // 전일대비가 있으면 순위는 기본 색상
                            : (priceIsNegative ? '#1976d2' : '#d32f2f'); // 전일대비가 없으면 가격 부호로 색상 결정
                          
                          return (
                            <TableCell key={date} align="center">
                              {dateData ? (
                                <Box>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 'bold',
                                      color: rankColor
                                    }}
                                  >
                                    {dateData.rank}
                                  </Typography>
                                  {hasChangeRate && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isUp ? '#d32f2f' : isDown ? '#1976d2' : 'text.secondary',
                                        fontWeight: 'bold',
                                        display: 'block'
                                      }}
                                    >
                                      {isUp ? '+' : ''}{changeRateValue.toFixed(2)}%
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  추세분석 데이터가 없습니다.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* 거래대금 상위 테이블 - 완전 분리된 Paper */}
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {currentDate || todayDateString} 거래대금 상위
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  type="date"
                  label="날짜 선택"
                  value={selectedDate}
                  onChange={handleDateChange}
                  disabled={loading || !isSubscribed}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: getTodayDateInput, // 오늘 이후 날짜 선택 불가
                  }}
                  size="small"
                  sx={{ minWidth: 150 }}
                />
                {loading && (
                  <CircularProgress size={20} />
                )}
              </Box>
            </Box>

            {!isSubscribed && (
              <Alert severity="info">
                나의환경설정에서 구독관리에서 구독신청하세요
              </Alert>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {topStocks.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.keys(stocksByTheme).sort().map((theme) => (
                  <Accordion key={theme} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {theme}
                        </Typography>
                        <Chip 
                          label={`${stocksByTheme[theme].length}개`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer
                        sx={{
                          border: 'none',
                          boxShadow: 'none',
                          bgcolor: 'transparent',
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>순위</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>종목명</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>현재가</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>전일대비</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>현재거래량</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>전일거래량</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>감소여부</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>거래대금</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stocksByTheme[theme].map((stock, index) => {
                              const isDecreased = stock.isDecreased || false;
                              const decreaseRate = stock.decreaseRate || 0;
                              const price = stock.price || 0;
                              const isPriceNegative = price < 0 || String(price).startsWith('-');
                              const prevChange = stock.prevChange || 0;
                              const isPrevChangeNegative = prevChange < 0 || String(prevChange).startsWith('-');
                              
                              return (
                                <TableRow key={stock.stockCode} hover>
                                  <TableCell align="center">{index + 1}</TableCell>
                                  <TableCell align="center">{stock.stockName}</TableCell>
                                  <TableCell align="center">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: isPriceNegative ? '#1976d2' : 'inherit',
                                        fontWeight: isPriceNegative ? 'bold' : 'normal'
                                      }}
                                    >
                                      {formatNumber(price)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: isPrevChangeNegative ? '#1976d2' : '#d32f2f',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {prevChange > 0 ? '+' : ''}{formatNumber(prevChange)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">{formatNumber(stock.volume)}</TableCell>
                                  <TableCell align="right">{formatNumber(stock.prevVolume || 0)}</TableCell>
                                  <TableCell align="center">
                                    {stock.prevVolume > 0 ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDecreased ? '#1976d2' : '#d32f2f',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {isDecreased ? '감소' : '증가'} ({Math.abs(decreaseRate).toFixed(2)}%)
                                      </Typography>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                  <TableCell align="right">{formatNumber(stock.tradingValue)}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}

            {!loading && topStocks.length === 0 && isSubscribed && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  오늘 날짜의 거래대금 상위 종목 데이터가 없습니다.
                  <br />
                  (매일 21시에 자동으로 저장됩니다)
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AutoTradingStrategy;
